/**
 * DonutChart Component
 * Renders a donut chart visualization for findings summary with custom center label and legend
 * Design based on Figma specifications
 */

import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import type { DonutChartDataItem } from "../types";
import { SEVERITY_COLORS, SEVERITY_LABELS } from "../constants";

interface DonutChartProps {
  critical: number;
  high: number;
  medium: number;
  low: number;
  criticalPercentage: number;
  highPercentage: number;
  mediumPercentage: number;
  lowPercentage: number;
  total: number;
  onSeverityClick?: (severity: "Critical" | "High" | "Medium" | "Low") => void;
}

// CSS for chart elements
const chartStyles = `
  .donut-chart-container *:focus {
    outline: none !important;
    box-shadow: none !important;
  }
  .donut-chart-cell-clickable {
    cursor: pointer;
    transition: opacity 0.2s;
  }
  .donut-chart-cell-clickable:hover {
    opacity: 0.8;
  }
`;

export function DonutChart({
  critical,
  high,
  medium,
  low,
  criticalPercentage,
  highPercentage,
  mediumPercentage,
  lowPercentage,
  total,
  onSeverityClick
}: DonutChartProps) {
  // Transform data for Recharts
  const chartData: DonutChartDataItem[] = [
    { name: SEVERITY_LABELS.critical, value: critical, color: SEVERITY_COLORS.critical },
    { name: SEVERITY_LABELS.high, value: high, color: SEVERITY_COLORS.high },
    { name: SEVERITY_LABELS.medium, value: medium, color: SEVERITY_COLORS.medium },
    { name: SEVERITY_LABELS.low, value: low, color: SEVERITY_COLORS.low },
  ];

  // Legend data with counts and percentages
  const legendData = [
    { label: SEVERITY_LABELS.critical, color: SEVERITY_COLORS.critical, count: critical, percentage: criticalPercentage },
    { label: SEVERITY_LABELS.high, color: SEVERITY_COLORS.high, count: high, percentage: highPercentage },
    { label: SEVERITY_LABELS.medium, color: SEVERITY_COLORS.medium, count: medium, percentage: mediumPercentage },
    { label: SEVERITY_LABELS.low, color: SEVERITY_COLORS.low, count: low, percentage: lowPercentage },
  ];

  // When total is 0, show a gray placeholder ring
  const hasData = total > 0;
  const emptyStateData = [{ name: "No Data", value: 1, color: "#E5E7EB" }];

  return (
    <div className="flex flex-row items-center gap-3 max-w-[540px] h-72">
      <style>{chartStyles}</style>
      {/* Left Column: Donut Chart */}
      <div className="donut-chart-container flex flex-row items-center justify-center px-9 py-12 w-[264px] h-full relative">
        {/* Recharts Donut Chart */}
        <ResponsiveContainer width={192} height={192}>
          <PieChart>
            <Pie
              data={hasData ? chartData : emptyStateData}
              cx="50%"
              cy="50%"
              innerRadius={72}
              outerRadius={96}
              dataKey="value"
              stroke="#FFFFFF"
              strokeWidth={2}
              startAngle={90}
              endAngle={-270}
              onClick={(data) => {
                if (hasData && onSeverityClick && data && data.name) {
                  onSeverityClick(data.name as "Critical" | "High" | "Medium" | "Low");
                }
              }}
            >
              {hasData ? (
                chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    className={onSeverityClick ? "donut-chart-cell-clickable" : ""}
                  />
                ))
              ) : (
                <Cell key="empty" fill="#E5E7EB" />
              )}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Center Label - Positioned Absolutely */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-4xl font-bold leading-[48px] text-[#0F172B]">
            {total}
          </span>
          <span className="text-sm font-normal leading-5 text-[#314158]">
            Total Findings
          </span>
        </div>
      </div>

      {/* Right Column: Legend */}
      <div className="flex flex-col justify-center items-start px-4 gap-6 w-[264px]">
        {legendData.map((item) => {
          const handleClick = () => {
            if (onSeverityClick) {
              onSeverityClick(item.label as "Critical" | "High" | "Medium" | "Low");
            }
          };

          const handleKeyDown = (e: React.KeyboardEvent) => {
            if (onSeverityClick && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onSeverityClick(item.label as "Critical" | "High" | "Medium" | "Low");
            }
          };

          return (
            <div
              key={item.label}
              className={`flex flex-row items-center gap-2 w-full h-5 ${
                onSeverityClick ? "cursor-pointer hover:opacity-80 transition-opacity" : ""
              }`}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              role={onSeverityClick ? "button" : undefined}
              tabIndex={onSeverityClick ? 0 : undefined}
            >
              {/* Color Dot */}
              <div
                className="w-3 h-3 rounded-full flex-none"
                style={{ backgroundColor: item.color }}
                aria-hidden="true"
              />

              {/* Label */}
              <span className="text-sm font-normal leading-5 text-[#314158] flex-grow">
                {item.label}
              </span>

              {/* Count with Percentage */}
              <span className="text-sm font-medium leading-5 text-[#314158] text-right flex-none">
                {item.count} ({item.percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
